/**
 * lv-variant-gallery.js
 *
 * Groups the product media gallery by the colour written at the start of each
 * image alt, e.g. "Blue — Wrap by Lanvelle (1/6)" -> "blue".
 *
 * Deliberately PURE CLIENT SIDE and non-destructive:
 *   - no DOM node is added or removed (items are only shown / hidden);
 *   - Dawn's own product-form, variant and media-gallery scripts are untouched;
 *   - the gallery slider is recalculated through Dawn's own resetPages() so the
 *     prev / next arrows and the page counter stay correct.
 *
 * Therefore, even if this file fails to run, add-to-cart / buy-it-now keep working.
 *
 * Collapse guard: the currently active slide is never hidden. In the thumbnail
 * layout Dawn renders only the .is-active media item, so hiding it would leave
 * zero visible slides, the media column would collapse to 0 height and the
 * thumbnails underneath would jump up. The active marker is moved to the first
 * slide of the newly selected colour *before* anything is hidden.
 */
(function () {
  'use strict';

  var HIDDEN_CLASS = 'lv-media-hidden';
  var STYLE_ID = 'lv-media-hidden-style';
  var COLOR_SPLIT = /\s+(?:—|\||–)\s+/;
  var ITEM_SELECTOR = 'li.product__media-item, .thumbnail-list__item';

  var pending = false;
  var lastColor = null;

  function norm(value) {
    return (value || '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function colorFromAlt(element) {
    var img = element && element.querySelector('img[alt]');
    if (!img || !img.alt) return null;
    var head = norm(img.alt.split(COLOR_SPLIT)[0]);
    return head || null;
  }

  function selectedColor() {
    var root = document.querySelector('variant-selects, variant-radios');
    if (!root) return null;
    var select = root.querySelector('select');
    if (select && select.value) return norm(select.value);
    var checked = root.querySelector('input[type="radio"]:checked');
    if (checked) return norm(checked.value);
    return null;
  }

  function ensureStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = '.' + HIDDEN_CLASS + '{display:none !important;}';
    document.head.appendChild(style);
  }

  function resetSlider(slider) {
    try {
      if (slider && typeof slider.resetPages === 'function') slider.resetPages();
    } catch (error) {
      // never let a slider hiccup break the page
    }
  }

  function isActiveItem(element) {
    if (!element) return false;
    if (element.classList.contains('is-active')) return true;
    return Boolean(element.querySelector && element.querySelector('[aria-current]'));
  }

  function apply(force) {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    ensureStyle();

    var items = gallery.querySelectorAll(ITEM_SELECTOR);
    if (!items.length) return;

    var color = selectedColor();

    // Pass 1: does the selected colour actually have images? If not, show all
    // (a colour with no photos must never leave an empty gallery).
    var colors = new Map();
    var matchable = 0;
    var matching = 0;

    Array.prototype.forEach.call(items, function (item) {
      var itemColor = colorFromAlt(item);
      colors.set(item, itemColor);
      if (!itemColor) return;
      matchable += 1;
      if (color && itemColor === color) matching += 1;
    });

    var filtering = Boolean(color && matchable && matching);
    var viewer = gallery.querySelector('[id^="GalleryViewer"]');

    // Step 1 - if the active slide belongs to another colour, move the marker to
    // the first slide of the selected colour *before* hiding anything. This is
    // what keeps at least one slide visible at all times.
    if (filtering && viewer) {
      var active = viewer.querySelector('[data-media-id].is-active');
      var activeColor = active ? colors.get(active) : null;

      if (active && activeColor && activeColor !== color) {
        var target = null;
        Array.prototype.forEach.call(viewer.querySelectorAll('[data-media-id]'), function (element) {
          if (!target && colors.get(element) === color) target = element;
        });
        if (target && typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(target.dataset.mediaId, false);
        }
      }
    }

    var changed = force === true || color !== lastColor;

    // Step 2 - show / hide. The active item is never hidden.
    Array.prototype.forEach.call(items, function (item) {
      var itemColor = colors.get(item);
      var hide = Boolean(filtering && itemColor && itemColor !== color);
      if (hide && isActiveItem(item)) hide = false;

      if (item.classList.contains(HIDDEN_CLASS) !== hide) {
        item.classList.toggle(HIDDEN_CLASS, hide);
        changed = true;
      }
    });

    // Only re-measure the sliders when the visible set really changed - every
    // resetPages() reflows the gallery, so needless calls would add jank.
    if (changed) {
      lastColor = color;
      resetSlider(viewer);
      resetSlider(gallery.querySelector('[id^="GalleryThumbnails"]'));
    }
  }

  function schedule(force) {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(function () {
      pending = false;
      window.setTimeout(function () {
        apply(force === true);
      }, 0);
    });
  }

  function init() {
    var gallery = document.querySelector('media-gallery');
    if (!gallery) return;

    apply(true);

    // Dawn re-fetches and re-orders the gallery on every variant change.
    new MutationObserver(function () {
      schedule(false);
    }).observe(gallery, { childList: true, subtree: true });

    document.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (target && target.closest && target.closest('variant-selects, variant-radios')) schedule(true);
      },
      true
    );

    document.addEventListener('variant:changed', function () {
      schedule(true);
    });
    window.addEventListener('resize', function () {
      schedule(true);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
